"use client";
import type { ReactNode } from "react";
import type { SurveyQuestion } from "@/types/survey";
import { motion } from "motion/react";
import { getRatingWordConfig } from "./ratingDefaults";

interface RatingWordQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  uiStrings?: {
    ratingWordLabels: readonly string[];
  };
}

const DEFAULT_BACKGROUNDS = [
  "var(--color-alias-error)",
  "var(--color-alias-warning)",
  "var(--color-alias-warning-light)",
  "var(--color-alias-success-light)",
  "var(--color-alias-success-dark)",
];

const motionTransition = { type: "spring" as const, stiffness: 400, damping: 17, duration: 0.2 };

export default function RatingWordQuestion({
  question,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  brandColor = "var(--color-alias-info)",
  isSelected,
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  uiStrings,
}: RatingWordQuestionProps) {
  const { words, backgroundColor, textColor: textColorOpt, borderColor } =
    getRatingWordConfig(question, uiStrings?.ratingWordLabels);
  const textColor = textColorOpt ?? "var(--color-alias-foreground-on-color)";
  // Selection ring stays at 2px in textColor; an optional 1px borderColor
  // applies in the unselected state (mirrors RatingQuestion behavior).
  const baseBorder = borderColor ? `1px solid ${borderColor}` : "2px solid transparent";
  const selectedBorder = `2px solid ${textColor}`;

  const options = words.map((t, i) => ({ v: i + 1, t }));

  const handleAnswerChange = (value: string | number) => {
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, value);
    } else if (onAnswerChange) {
      onAnswerChange(question, value.toString());
    }
  };

  const isItemSelected = (value: number) => {
    if (isPreview) {
      return selectedValue === value;
    }
    return isSelected ? isSelected(question, value) : false;
  };

  const itemStyle = (idx: number, value: number) => ({
    color: textColor,
    border: isItemSelected(value) ? selectedBorder : baseBorder,
    backgroundColor: backgroundColor ?? DEFAULT_BACKGROUNDS[idx],
    boxShadow: isItemSelected(value) ? "var(--box-shadow-md)" : "none",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: 1.2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center" as const,
  });

  const motionProps = (value: number) => ({
    animate: { filter: isItemSelected(value) ? "brightness(1.2)" : "brightness(1)" },
    whileHover: { scale: 1.05, boxShadow: "var(--box-shadow-md)" },
    whileTap: { scale: 0.98 },
    transition: motionTransition,
  });

  const renderItem = (idx: number, o: { v: number; t: string }): ReactNode =>
    isPreview ? (
      <motion.button
        key={o.v}
        onClick={() => handleAnswerChange(o.v)}
        className="h-12 w-full rounded-md font-semibold whitespace-pre-line px-1"
        style={itemStyle(idx, o.v)}
        {...motionProps(o.v)}
      >
        {o.t}
      </motion.button>
    ) : (
      <div key={o.v}>
        <input
          type="radio"
          id={`rating-${question.id}-${o.v}`}
          value={o.v}
          name={`rating-${question.id}`}
          className="sr-only"
          checked={isItemSelected(o.v)}
          onChange={() => handleAnswerChange(o.v)}
        />
        <motion.label
          htmlFor={`rating-${question.id}-${o.v}`}
          className="h-12 w-full rounded-md font-semibold whitespace-pre-line flex items-center justify-center cursor-pointer text-center px-1"
          style={itemStyle(idx, o.v)}
          {...motionProps(o.v)}
          aria-label={o.t.replace(/\n/g, " ")}
        >
          {o.t}
        </motion.label>
      </div>
    );

  return (
    <div className="mb-6">
      <div className="text-sm font-medium mb-2 text-center" style={{ color: "var(--sl-text, var(--color-alias-neutral-darkest))" }}>
        {question.text}
      </div>
      <div
        className="mx-auto w-full"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: "0.5rem",
          maxWidth: "32rem",
        }}
      >
        {options.map((o, idx) => renderItem(idx, o))}
      </div>
    </div>
  );
}
